import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './RichTextEditor.css';

const RichTextEditor = ({ 
  value, 
  onChange, 
  onBlur, 
  className = '', 
  placeholder = 'Enter text here...',
  autoFocus = false,
  renderInMainContainer = false // New prop to control where the editor is rendered
}) => {
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(true);
  const [selectionState, setSelectionState] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [currentFormat, setCurrentFormat] = useState({
    bold: false,
    italic: false,
    underline: false,
    align: 'left',
    format: 'p',
    link: false
  });

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || `<p>${placeholder}</p>`;
      
      // Auto-focus if requested
      if (autoFocus) {
        setTimeout(() => {
          editorRef.current.focus();
          
          // Set cursor at the end
          const range = document.createRange();
          const sel = window.getSelection();
          
          if (editorRef.current.childNodes.length > 0) {
            const lastChild = editorRef.current.lastChild;
            range.setStartAfter(lastChild);
            range.collapse(true);
            
            sel.removeAllRanges();
            sel.addRange(range);
            
            // Trigger a click to ensure the editor fully activates
            editorRef.current.click();
          }
        }, 100);
      }
    }
  }, []);

  // Initialize position for draggable editor
  useEffect(() => {
    if (renderInMainContainer) {
      // Center the editor initially
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      setPosition({
        x: (screenWidth - 800) / 2, // 800px is max-width of editor
        y: (screenHeight - 600) / 2 // 600px is height of editor
      });
    }
  }, [renderInMainContainer]);

  // Update editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && value !== undefined && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Add drag event listeners for main container rendering
  useEffect(() => {
    if (renderInMainContainer) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [renderInMainContainer, isDragging]);

  // Handle mouse down for dragging
  const handleDragStart = (e) => {
    if (e.target.closest('.editor-header')) {
      setIsDragging(true);
      setDragStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.preventDefault();
    }
  };

  // Handle mouse move for dragging
  const handleDragMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStartPos.x,
        y: e.clientY - dragStartPos.y
      });
      e.preventDefault();
    }
  };

  // Handle mouse up for dragging
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Handle clicking the close button or Apply Changes button
  const handleClose = () => {
    if (onBlur) {
      onBlur(editorRef.current.innerHTML);
    }
  };

  // Handle saving selection state for when toolbar buttons are clicked
  const saveSelection = () => {
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        setSelectionState(sel.getRangeAt(0));
      }
    }
  };

  // Restore selection state when needed
  const restoreSelection = () => {
    if (selectionState) {
      if (window.getSelection) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(selectionState);
      }
    }
  };

  // Execute commands while preserving selection
  const execCommand = (command, value = null) => {
    // Ensure editor has focus
    editorRef.current.focus();
    
    // Restore any saved selection
    restoreSelection();
    
    // Get current selection
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const isCollapsed = range.collapsed; // true if no text is selected
    
    if (isCollapsed) {
      // No text selected - create a new span with the formatting
      const span = document.createElement('span');
      range.insertNode(span);
      range.selectNodeContents(span);
      document.execCommand(command, false, value);
      
      // Move cursor inside the formatted span
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Text is selected - apply formatting only to selection
      document.execCommand(command, false, value);
    }
    
    // Update content and save new selection
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    saveSelection();
    updateFormatState();
  };

  // Update formatting info to change active buttons in toolbar
  const updateFormatState = () => {
    try {
      const isBold = document.queryCommandState('bold');
      const isItalic = document.queryCommandState('italic');
      const isUnderline = document.queryCommandState('underline');
      
      let alignment = 'left';
      if (document.queryCommandState('justifyCenter')) alignment = 'center';
      if (document.queryCommandState('justifyRight')) alignment = 'right';
      if (document.queryCommandState('justifyFull')) alignment = 'justify';
      
      let format = 'p';
      const formatMapping = {
        'h1': 'heading 1',
        'h2': 'heading 2', 
        'h3': 'heading 3',
        'h4': 'heading 4',
        'h5': 'heading 5',
        'h6': 'heading 6',
        'pre': 'pre',
        'blockquote': 'blockquote'
      };
      
      const parentElement = window.getSelection().anchorNode.parentElement;
      if (parentElement) {
        for (const [tag, formatName] of Object.entries(formatMapping)) {
          if (parentElement.closest(tag)) {
            format = tag;
            break;
          }
        }
      }
      
      setCurrentFormat({
        bold: isBold,
        italic: isItalic,
        underline: isUnderline,
        align: alignment,
        format,
        link: !!parentElement.closest('a')
      });
    } catch (err) {
      // Silently fail if selection is outside the editor
    }
  };

  // Event handlers
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      execCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
    saveSelection();
    updateFormatState();
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    saveSelection();
    updateFormatState();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Handle link insertion
  const handleInsertLink = () => {
    const url = prompt('Enter URL:', 'https://');
    if (url) {
      execCommand('createLink', url);
    }
  };

  // Handle selection changes
  const handleSelectionChange = () => {
    saveSelection();
    updateFormatState();
  };
  
  // Handle stopping propagation of all mouse events to prevent closing when clicking inside
  const handleEditorClick = (e) => {
    e.stopPropagation();
  };

  // The actual editor component
  const editorComponent = (
    <div 
      ref={editorContainerRef}
      className={`rich-text-editor ${className} ${renderInMainContainer ? 'draggable-editor' : ''}`}
      style={renderInMainContainer ? {
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 99999,
        width: '80%',
        maxWidth: '800px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        borderRadius: '8px',
        border: '1px solid #2d2d3d',
        overflow: 'hidden'
      } : {}}
      onMouseDown={renderInMainContainer ? handleDragStart : undefined}
      onClick={handleEditorClick}
    >
      {renderInMainContainer && (
        <div className="editor-header" style={{
          padding: '12px 16px',
          background: '#1a1a2a',
          borderBottom: '1px solid #2d2d3d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'move'
        }}>
          <h3 style={{margin: 0, fontWeight: 500, fontSize: '18px'}}>Rich Text Editor</h3>
          <button 
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#a0a0a6',
              cursor: 'pointer',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }}
            onClick={handleClose}
          >
            ×
          </button>
        </div>
      )}
      <div className="editor-toolbar">
        <button 
          type="button" 
          onClick={() => execCommand('bold')}
          title="Bold"
          className={currentFormat.bold ? 'active' : ''}
        >
          <i className="format-icon">B</i>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('italic')}
          title="Italic"
          className={currentFormat.italic ? 'active' : ''}
        >
          <i className="format-icon">I</i>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('underline')}
          title="Underline" 
          className={currentFormat.underline ? 'active' : ''}
        >
          <i className="format-icon">U</i>
        </button>
        <div className="toolbar-divider" />

        <button 
          type="button" 
          onClick={() => execCommand('justifyLeft')}
          title="Align Left"
          className={currentFormat.align === 'left' ? 'active' : ''}
        >
          <i className="format-icon">←</i>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('justifyCenter')}
          title="Center"
          className={currentFormat.align === 'center' ? 'active' : ''}
        >
          <i className="format-icon">↔</i>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('justifyRight')}
          title="Align Right"
          className={currentFormat.align === 'right' ? 'active' : ''}
        >
          <i className="format-icon">→</i>
        </button>
        <div className="toolbar-divider" />

        <select 
          onChange={(e) => execCommand('formatBlock', e.target.value)}
          className="format-select"
          value={currentFormat.format}
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="h5">Heading 5</option>
          <option value="h6">Heading 6</option>
          <option value="pre">Code Block</option>
          <option value="blockquote">Quote</option>
        </select>
        
        <div className="toolbar-divider" />
        
        <button 
          type="button" 
          onClick={() => execCommand('insertUnorderedList')}
          title="Bullet List"
        >
          <i className="format-icon">•</i>
        </button>
        <button 
          type="button" 
          onClick={() => execCommand('insertOrderedList')}
          title="Numbered List"
        >
          <i className="format-icon">1.</i>
        </button>
        
        <div className="toolbar-divider" />
        
        <button 
          type="button" 
          onClick={handleInsertLink}
          title="Insert Link"
          className={currentFormat.link ? 'active' : ''}
        >
          <i className="format-icon">🔗</i>
        </button>
        
        <div className="toolbar-divider" />
        
        <div className="color-picker-container">
          <input 
            type="color" 
            className="color-picker" 
            title="Text Color"
            onChange={(e) => execCommand('foreColor', e.target.value)}
          />
          <span className="color-picker-label">Text</span>
        </div>
        
        <div className="color-picker-container">
          <input 
            type="color" 
            className="color-picker" 
            title="Background Color"
            onChange={(e) => execCommand('hiliteColor', e.target.value)}
          />
          <span className="color-picker-label">BG</span>
        </div>
      </div>
      
      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setShowToolbar(true)}
        onMouseUp={handleSelectionChange}
        onKeyUp={handleSelectionChange}
        onClick={handleEditorClick}
      />
      
      {renderInMainContainer && (
        <div style={{
          padding: '16px',
          borderTop: '1px solid #2d2d3d',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button 
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onClick={handleClose}
          >
            Apply Changes
          </button>
        </div>
      )}
    </div>
  );
  
  // Use portal for main container rendering
  if (renderInMainContainer) {
    const portalContent = (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 99998,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        onClick={handleEditorClick}
      >
        {editorComponent}
      </div>
    );
    
    return ReactDOM.createPortal(
      portalContent,
      document.querySelector('.main-container') || document.body
    );
  }
  
  // Regular rendering for in-place editor
  return editorComponent;
};

export default RichTextEditor; 